import type { DashFilterContext, DashFilterGroup, DashFilterValues } from "./types";

export function matchesDashSearch(haystack: string, q: string): boolean {
  const needle = String(q || "").trim().toLowerCase();
  if (!needle) return true;
  return String(haystack || "").toLowerCase().includes(needle);
}

export function dashHaystack(...parts: unknown[]): string {
  return parts
    .map((p) => (p == null ? "" : String(p)))
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

export function dashFilterValuesActive(values: DashFilterValues | undefined): boolean {
  if (!values) return false;
  return Object.values(values).some((v) => Array.isArray(v) && v.length > 0);
}

export function dashFilterIsActive(search: string, values?: DashFilterValues): boolean {
  return Boolean(String(search || "").trim()) || dashFilterValuesActive(values);
}

export function countActiveDashFilters(search: string, values?: DashFilterValues): number {
  let n = String(search || "").trim() ? 1 : 0;
  if (!values) return n;
  for (const v of Object.values(values)) {
    if (Array.isArray(v) && v.length) n++;
  }
  return n;
}

export function emptyDashFilterValues(groups?: DashFilterGroup[]): DashFilterValues {
  const out: DashFilterValues = {};
  for (const g of groups || []) out[g.id] = [];
  return out;
}

export function matchDashFilterGroups<Row>(
  row: Row,
  values: DashFilterValues,
  groups: DashFilterGroup[] | undefined,
  groupValue?: (row: Row, groupId: string) => string | string[] | null | undefined
): boolean {
  if (!groups?.length || !groupValue) return true;
  for (const g of groups) {
    const selected = values[g.id];
    if (!selected?.length) continue;
    const raw = groupValue(row, g.id);
    const ids = Array.isArray(raw) ? raw.map(String) : raw == null || raw === "" ? [] : [String(raw)];
    if (!selected.some((id) => ids.includes(id))) return false;
  }
  return true;
}

export function matchDashTableRow<Row>(
  row: Row,
  ctx: DashFilterContext,
  opts: {
    haystack?: (row: Row) => string;
    groupValue?: (row: Row, groupId: string) => string | string[] | null | undefined;
    groups?: DashFilterGroup[];
    rowMatches?: (row: Row, ctx: DashFilterContext) => boolean;
  }
): boolean {
  if (opts.rowMatches) return opts.rowMatches(row, ctx);
  if (opts.haystack && !matchesDashSearch(opts.haystack(row), ctx.search)) return false;
  return matchDashFilterGroups(row, ctx.values, opts.groups, opts.groupValue);
}

export function boolMapToSelected<T extends string>(map: Record<T, boolean>, keys: readonly T[]): string[] {
  const on = keys.filter((k) => map[k]);
  if (on.length === 0 || on.length === keys.length) return [];
  return on;
}

export function selectedToBoolMap<T extends string>(
  selected: string[] | undefined,
  keys: readonly T[]
): Record<T, boolean> {
  if (!selected?.length) {
    return Object.fromEntries(keys.map((k) => [k, true])) as Record<T, boolean>;
  }
  return Object.fromEntries(keys.map((k) => [k, selected.includes(k)])) as Record<T, boolean>;
}
